import { inject, Injectable, Signal } from "@angular/core";
import { BehaviorSubject, distinctUntilChanged, map, Observable } from "rxjs";
import { AuthorizedUser } from "../../models";
import { ENV_FILE_TOKEN, InitService, TranslationService } from "gast-common-ng2";
import { CommonPortalUrlService } from "../url";
import { AuthenticationDto, UserAuthorityDto } from "../../dto";
import { AuthService } from "./auth.service";
import { PORTAL_ANY_AUTHORIZING_ROLES_TOKEN, PortalQueryParams } from "../../constants";
import { toSignal } from "@angular/core/rxjs-interop";
import { UserModel } from "../../../header";

@Injectable()
export class UserAuthorizedService {
  readonly #translationService = inject(TranslationService);
  readonly #portalUrlService = inject(CommonPortalUrlService);
  readonly #initService = inject(InitService);
  readonly #authService = inject(AuthService);
  readonly #AUTHORIZING_ROLES: string[] | null = inject(PORTAL_ANY_AUTHORIZING_ROLES_TOKEN, { optional: true });
  readonly #envConfig = inject(ENV_FILE_TOKEN);
  readonly #currentAuthorizedUser$$ = new BehaviorSubject<AuthorizedUser | null>(null);

  /**
   * Emits an {@link AuthorizedUser} if the user was authorized or null if not.
   */
  authorizedUser$: Observable<AuthorizedUser | null> = this.#currentAuthorizedUser$$.pipe(
    distinctUntilChanged(
      (u1: AuthorizedUser | null, u2: AuthorizedUser | null): boolean =>
        (u1 === null && u2 === null) ||
        (u1 !== null && u2 !== null && u1.userName === u2.userName && u1.userId === u2.userId)
    )
  );

  /**
   * Emits an {@link UserModel} signal if the user was authorized or null if not.
   */
  authorizedUserModel: Signal<UserModel | null> = toSignal(
    this.authorizedUser$.pipe(
      map((authorizedUser) => {
        if (authorizedUser) {
          const userModel: UserModel = {
            username: authorizedUser.userName,
          };
          return userModel;
        } else {
          return null;
        }
      })
    ),
    { initialValue: null }
  );

  /**
   * Sets the authorized user state.
   * @param authorizedUser {@link AuthorizedUser | null}
   */
  updateAuthorizedUser(authorizedUser: AuthorizedUser | null): void {
    this.#currentAuthorizedUser$$.next(authorizedUser);
  }

  /**
   * Checks if user is authenticated.
   * @param user {@link AuthenticationDto}
   */
  isAuthenticated(user: AuthenticationDto): boolean {
    return !!user.name && user.authenticated;
  }

  /**
   * Checks if user is authorized
   * @param user {@link AuthenticationDto}
   */
  isAuthorized(user: AuthenticationDto): boolean {
    if (this.#AUTHORIZING_ROLES && this.#AUTHORIZING_ROLES.length > 0) {
      return user.authorities.some((a: UserAuthorityDto) => this.#AUTHORIZING_ROLES!.includes(a.authority));
    }
    return true;
  }

  /*
    Redirects to the portal account login page.
   */
  login(): void {
    window.location.href = this.#buildLoginUrl();
  }

  /*
    Performs a logout api call and redirects to the login page without redirect param.
    The redirect param is not passt to avoid the redirect loop.
   */
  logout(): void {
    const loginURL = new URL(`${this.#envConfig.accountPortalUrl}login`);
    const product = this.#initService.getProductFromLocalStorage();
    if (product) {
      loginURL.searchParams.set(PortalQueryParams.PRODUCT_ID, product);
    }
    const lang = this.#resolveLangParam();
    if (lang) {
      loginURL.searchParams.set(PortalQueryParams.LANGUAGE_CODE, lang);
    }
    this.#authService.logout(loginURL.toString());
  }

  #buildLoginUrl(): string {
    return this.#portalUrlService.getPortalAccountLoginUrl(
      this.#initService.getProductFromLocalStorage(),
      this.#translationService
    );
  }

  #resolveLangParam(): string {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has(PortalQueryParams.LANGUAGE_CODE)) {
      return currentUrl.searchParams.get(PortalQueryParams.LANGUAGE_CODE)!;
    } else {
      return this.#translationService.getCurrentLanguageCode();
    }
  }
}
